import React, { useRef, useEffect, useState, useMemo } from "react";
import Icon from "../ui/Icon";
import SharePriceChart from "./SharePriceChart";
import SharePriceTabQuadrata from "./SharePriceTabQuadrata";
import { classNames, formatDateApi, checkEmptyObject, compareDate, compareSameKeyObject } from "../../utils";
import DownloadButton from "../DownloadButton";
import { useTemplateSetting, useCompanySettings } from "../../hooks/useCompanySettings";
import { useInstrument } from "../../hooks/useInstruments";
import useSaveLinkDownload from "../../hooks/useSaveLinkDownload";
import SharePriceSelection from "./SharePriceSelection";
import useListTradeBaseOnPeriod from "../../hooks/useListTradeBaseOnPeriod";
import { arrayColor, TOOL_TEMPLATE, TAB, DATE_SCREEN_READER } from "../../constant/common";
import { useDispatch, useSelector } from "react-redux";
import { getHistoricalPricesAction } from "../../actions";
import { i18n } from "@euroland/libs";
import { formatDate } from "@euroland/libs";
import { getHistoricalPricesSelector } from "../../store/slice/trade";
import {
  colorBlindModeSelector,
  instrumentsSettingsSelector,
  peersSettingsSelector,
} from "../../store/slice/settings/selectors";
import { getCurrencyLabel, getTickerPatternById } from "../../store/utils";
import { TableData } from "@euroland/react-table";
import { getSvgOfDashStyle } from "@euroland/react-high-chart";
import { renderValueTable } from "../../utils/format-number";
import SwichAction from "../SwichAction";

const createTableData = (dataTable = [], settingInstrument = [], colorBlindMode) => {
  const keyTableSharePriceTab = [
    {
      nameQuadrata: i18n.translate("changePeriodlabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("changePeriodlabelTableSharePriceVictor"),
      keyApi: "changeOfPeriodPercent",
      keyDate: "changeOfPeriodPercentDate",
    },
    {
      nameQuadrata: i18n.translate("highPriceLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("highPriceLabelTableSharePriceVictor"),
      keyApi: "highestPrice",
      keyDate: "highestPriceDate",
    },
    {
      nameQuadrata: i18n.translate("lowPriceLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("lowPriceLabelTableSharePriceVictor"),
      keyApi: "lowestPrice",
      keyDate: "lowestPriceDate",
    },
    {
      nameQuadrata: i18n.translate("highVolumeLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("highVolumeLabelTableSharePriceVictor"),
      keyApi: "highestVolume",
      keyDate: "highestVolumeDate",
    },
    {
      nameQuadrata: i18n.translate("lowVolumeLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("lowVolumeLabelTableSharePriceVictor"),
      keyApi: "lowestVolume",
      keyDate: "lowestVolumeDate",
    },
    {
      nameQuadrata: i18n.translate("highDailyLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("highDailyLabelTableSharePriceVictor"),
      keyApi: "highestDailyChangePercent",
      keyDate: "highestDailyChangePercentDate",
    },
    {
      nameQuadrata: i18n.translate("lowDailyLabelTableSharePriceQuadrata"),
      nameVictor: i18n.translate("lowDailyLabelTableSharePriceVictor"),
      keyApi: "lowestDailyChangePercent",
      keyDate: "lowestDailyChangePercentDate",
    },
  ];
  const header = {
    column_name: { children: "Companies" },
  };
  dataTable.forEach((item, index) => {
    const itemInstrument = settingInstrument.find(x => x.id === item.instrumentId);
    header["column-" + item.instrumentId] = {
      children: (
        <>
          {colorBlindMode ? (
            getSvgOfDashStyle({
              color: itemInstrument?.color || arrayColor[index],
              lineWidth: 3,
              dashStyle: getTickerPatternById(item.instrumentId),
              isReactComponent: true,
            })
          ) : (
            <Icon
              name="rectangle"
              className=""
              style={
                itemInstrument?.color || arrayColor[index]
                  ? {
                      color: itemInstrument?.color || arrayColor[index],
                    }
                  : {}
              }
            ></Icon>
          )}

          <span style={{}} className="column-name__title">
            {item.ticker} <span>({item.marketAbbreviation})</span>
          </span>
        </>
      ),
    };
  });
  const body = keyTableSharePriceTab.map(item => {
    const columnTable = {
      column_name: {
        children: <span className="columnName__table">{item.nameVictor}</span>,
      },
    };
    dataTable.forEach(user => {
      columnTable["column-" + user.instrumentId] = {
        children: (
          <div
            className={classNames("text-table", {
              "text-table__high":
                (["highestPrice", "highestDailyChangePercent"].includes(item.keyApi) && user[item.keyApi]) ||
                (item.keyApi === "changeOfPeriodPercent" && user[item.keyApi] > 0),
              "text-table__low":
                (["lowestPrice", "lowestDailyChangePercent"].includes(item.keyApi) && user[item.keyApi]) ||
                (item.keyApi === "changeOfPeriodPercent" && user[item.keyApi] < 0),
            })}
          >
            <p>
              {item.keyApi === "currency"
                ? getCurrencyLabel(user[item.keyApi])
                : renderValueTable(item.keyApi, user)}
            </p>
            {
              user[item.keyDate] && (
                <small role="note" aria-label={formatDate(user[item.keyDate], "", DATE_SCREEN_READER)}>
                  {formatDate(user[item.keyDate], "shortDate")}
                </small>
              )
              // <small>{dayjs(user[item.keyDate]).format(shortDateFormat)}</small>
            }
          </div>
        ),
      };
    });

    return columnTable;
  });

  return { header, body };
};

const SharePriceTabContent = ({ currentTab, isChangeTab }) => {
  const template = useTemplateSetting();
  const colorBlindMode = useSelector(colorBlindModeSelector);
  const [{ selectedInstrument }] = useInstrument();
  const { enableExcelDownload } = useCompanySettings();

  const { saveLinkDownLoad } = useSaveLinkDownload();
  const historicalPrices = useSelector(getHistoricalPricesSelector);
  const prevShowData = useRef({});
  const firstRender = useRef(true);

  // Get setting instrument
  const instrumentSetting = useSelector(instrumentsSettingsSelector);
  const peerSetting = useSelector(peersSettingsSelector);

  const dispatch = useDispatch();
  const { getListTradeBaseOnPeriod, listTradeBaseOnPeriodState } = useListTradeBaseOnPeriod();
  const { loading, listTradeBaseOnPeriod } = listTradeBaseOnPeriodState;
  const [isSingle, setIsSingle] = useState(
    !(historicalPrices.ids.length > 1 || listTradeBaseOnPeriod.length > 1)
  );

  // when instrument search in historical tab dif in share-price tab, we fetch data
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else if (
      TAB.sharePrice === currentTab &&
      !checkEmptyObject(prevShowData.current) &&
      !compareSameKeyObject(prevShowData.current.prevSelectedInstrument, selectedInstrument)
    ) {
      fetchData(prevShowData.current, selectedInstrument);
    }
  }, [isChangeTab]);

  // when get data from server, change layout
  useEffect(() => {
    if (historicalPrices.ids.length > 1 || listTradeBaseOnPeriod.length > 1) setIsSingle(false);
    else setIsSingle(true);
  }, [listTradeBaseOnPeriod, historicalPrices]);

  const dataTable = useMemo(
    () => createTableData(listTradeBaseOnPeriod, [...instrumentSetting, ...peerSetting], colorBlindMode),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listTradeBaseOnPeriod, colorBlindMode]
  );

  const compareShowData = currentData => {
    const { currency = "", rangePickerVal = {} } = currentData;
    // Check prevShowData have no data
    if (checkEmptyObject(prevShowData.current)) return;
    const noChangeInstrument = compareSameKeyObject(
      prevShowData.current.prevSelectedInstrument,
      selectedInstrument
    );

    return (
      noChangeInstrument &&
      prevShowData.current.currency === currency &&
      compareDate(prevShowData.current.rangePickerVal.from, rangePickerVal.from, "day") &&
      compareDate(prevShowData.current.rangePickerVal.to, rangePickerVal.to, "day")
    );
  };

  const fetchData = (showData, instrumentTicker) => {
    prevShowData.current = { ...showData, prevSelectedInstrument: instrumentTicker };
    const { currency = "", rangePickerVal = {} } = showData;
    const shares = [];
    for (let x in instrumentTicker) {
      shares.push({
        id: instrumentTicker[x].instrumentId,
        isin: instrumentTicker[x].iSIN,
        marketId: instrumentTicker[x].marketID,
        currency: instrumentTicker[x].currencyCode,
      });
    }

    const dataQuery = {
      currency,
      fromDate: rangePickerVal?.from ? formatDateApi(rangePickerVal.from) : null,
      toDate: rangePickerVal?.to ? formatDateApi(rangePickerVal.to) : null,
      shares,
    };

    const historyDataQuery = {
      currency,
      fromDate: rangePickerVal?.from ? formatDateApi(rangePickerVal.from) : null,
      toDate: rangePickerVal?.to ? formatDateApi(rangePickerVal.to) : null,
      instrumentId: Object.keys(instrumentTicker),
    };

    getListTradeBaseOnPeriod(dataQuery);
    dispatch(getHistoricalPricesAction(historyDataQuery));

    if (enableExcelDownload) saveLinkDownLoad(rangePickerVal, currency);
  };

  const handleShowData = showData => {
    if (loading || compareShowData(showData)) return;
    fetchData(showData, selectedInstrument);
  };

  const isQuadrata = template === TOOL_TEMPLATE.quadrata;
  const isVictor = template === TOOL_TEMPLATE.victor;

  return (
    <>
      <div
        className={classNames("share-price", {
          "share-price__individual": isVictor && isSingle,
          "share-price__multiple": isVictor && !isSingle,
        })}
      >
        <SharePriceSelection
          onSubmit={handleShowData}
          isQuadrata={isQuadrata}
          isVictor={isVictor}
          loading={loading}
          renderValueTable={renderValueTable}
        />

        {isQuadrata ? (
          <SharePriceTabQuadrata
            isSingle={isSingle}
            listTradeBaseOnPeriodState={listTradeBaseOnPeriodState}
          />
        ) : (
          <>
            {listTradeBaseOnPeriod?.length > 0 && (
              <>
                <SwichAction />
                <div className="share-price__wrapper">
                  <SharePriceChart isSingle={isSingle}></SharePriceChart>
                  <TableData
                    className={classNames("share-price__table share-price-victor", {
                      "hidden-header": isSingle,
                    })}
                    caption={i18n.translate("noteTable")}
                    header={dataTable.header}
                    body={dataTable.body}
                    containerProps={{}}
                  />
                </div>
                <div className="share-price__download--outside">
                  <DownloadButton />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SharePriceTabContent;
